// Amplify brand colors and theme
export const amplifyTheme = {
  // Core colors
  primaryBlue: '#0066cc',
  primaryOrange: '#ff6b35',
  darkBlue: '#004499',
  lightBlue: '#e6f2ff',
  lightOrange: '#fff0ea',
  darkBackground: '#0a0a0a',
  lightGray: '#f5f7fa',
  borderColor: '#e1e8f0',

  // Gradients
  gradient: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
  orangeGradient: 'linear-gradient(135deg, #ff6b35 0%, #e54a1b 100%)',
  darkGradient: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',

  // Status colors
  success: '#22c55e',
  successDark: '#16a34a',
  warning: '#f59e0b',
  warningDark: '#d97706',
  error: '#ef4444',
  errorDark: '#dc2626',

  // Text colors
  textPrimary: '#1a1718',
  textSecondary: '#6b7280',
  textLight: '#9ca3af',
  textOnDark: '#ffffff',

  // Shadows
  shadowSm: '0 2px 4px rgba(0, 102, 204, 0.1)',
  shadowMd: '0 4px 12px rgba(0, 102, 204, 0.15)',
  shadowLg: '0 8px 24px rgba(0, 102, 204, 0.2)',
  shadowXl: '0 20px 40px rgba(0, 0, 0, 0.15)',
};

// Common style mixins
export const amplifyStyles: Record<string, any> = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: amplifyTheme.shadowMd,
    padding: '24px',
  },

  cardHover: {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      borderRadius: '12px',
      opacity: 0,
      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      background:
        'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(0, 102, 204, 0.06), transparent 40%)',
      pointerEvents: 'none',
    },
    ':hover': {
      transform: 'translateY(-4px) scale(1.01)',
      boxShadow: `0 12px 32px rgba(0, 102, 204, 0.3), 0 0 24px rgba(0, 102, 204, 0.2), inset 0 0 0 1px rgba(0, 102, 204, 0.1)`,
      backgroundColor: '#fafbfd',
      '&::after': {
        opacity: 1,
      },
    },
  },

  primaryButton: {
    background: amplifyTheme.gradient,
    color: 'white',
    borderRadius: '8px',
    padding: '12px 24px',
    fontWeight: '600',
    fontSize: '16px',
    boxShadow: amplifyTheme.shadowMd,
    transition: 'all 0.2s ease',
    border: 'none',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: amplifyTheme.shadowLg,
    },
  },

  secondaryButton: {
    backgroundColor: 'white',
    color: amplifyTheme.primaryBlue,
    border: `2px solid ${amplifyTheme.primaryBlue}`,
    borderRadius: '8px',
    padding: '10px 22px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: amplifyTheme.lightBlue,
      transform: 'scale(1.02)',
    },
  },

  dangerButton: {
    background: amplifyTheme.orangeGradient,
    color: 'white',
    borderRadius: '8px',
    padding: '10px 22px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
    transition: 'all 0.2s ease',
    border: 'none',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(255, 107, 53, 0.4)',
    },
  },

  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fafbfc',
    padding: '24px',
  },

  pageHeader: {
    marginBottom: '24px',
    padding: '24px',
    background: amplifyTheme.gradient,
    borderRadius: '12px',
    boxShadow: amplifyTheme.shadowLg,
    color: 'white',
  },

  pageTitle: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '8px',
    color: 'white',
  },

  pageSubtitle: {
    fontSize: '16px',
    opacity: 0.9,
    color: 'white',
  },

  statsCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    padding: '16px 24px',
    borderRadius: '8px',
    backdropFilter: 'blur(10px)',
  },

  badge: {
    borderRadius: '20px',
    padding: '4px 12px',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
};

// Animation keyframes
export const animations = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      transform: translateX(-20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
